import { BiddingIntradayOrderStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { EnumBadgeSpec } from '~/components/common/EnumStatusBadge'

/**
 * 時間前注文ステータス (約定軸) → 状態バッジ表示仕様。 約定履歴の約定ステータス列
 * (`ExecutionDetailTable`) が約定の値を上書きして使う。 一覧・入札履歴・詳細のステータスは
 * 3 軸を合成する `IntradayBidStatusCell` で出す (AC-BIN-035)。
 *
 * APPROVED (承認済) は「承認済」 と出さず遷移先の送信中で表す (AC-BIN-051。 予約中は送信軸で
 * 決まるため約定軸だけでは分からない)。 FAILED (送れるまで送り直している札) も送信中で表す
 * (「送信失敗」 の区分は無い、 #10680)。
 *
 * 部分約定は「約定が確定していない進行中」 なので `info`、 全量約定は望ましい終端
 * なので `positive`。 旧実装は両方を同じ teal で表していたため意味が分かれていな
 * かった。
 */
export const INTRADAY_STATUS_BADGE: Record<
  BiddingIntradayOrderStatus,
  EnumBadgeSpec
> = {
  [BiddingIntradayOrderStatus.UNSPECIFIED]: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.orderStatus.unspecified',
  },
  [BiddingIntradayOrderStatus.DRAFT]: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.orderStatus.draft',
  },
  [BiddingIntradayOrderStatus.PENDING]: {
    status: 'warning',
    labelKey: 'page:biddingIntraday.orderStatus.pending',
  },
  [BiddingIntradayOrderStatus.APPROVED]: {
    status: 'info',
    labelKey: 'page:biddingIntraday.orderStatus.sending',
  },
  [BiddingIntradayOrderStatus.PLACED]: {
    status: 'info',
    labelKey: 'page:biddingIntraday.orderStatus.placed',
  },
  [BiddingIntradayOrderStatus.PARTIAL_MATCHED]: {
    status: 'info',
    labelKey: 'page:biddingIntraday.orderStatus.partialMatched',
  },
  [BiddingIntradayOrderStatus.ALL_MATCHED]: {
    status: 'positive',
    labelKey: 'page:biddingIntraday.orderStatus.allMatched',
  },
  [BiddingIntradayOrderStatus.CANCELLED]: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.orderStatus.cancelled',
  },
  // 自動取消 (AC-BIN-017/035): 板に並んでいた札が約定ゼロのまま自動取消時刻に達した。
  // 取消と同じく失敗ではないので `neutral`。
  [BiddingIntradayOrderStatus.AUTO_CANCELLED]: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.orderStatus.autoCancelled',
  },
  [BiddingIntradayOrderStatus.FAILED]: {
    status: 'info',
    labelKey: 'page:biddingIntraday.orderStatus.sending',
  },
  // 未約定 (AC-BIN-019/036): GC 到達時点で板に並んでいた(約定累計ゼロ)。
  // 失敗ではないので `neutral` (SpotBidStatus.UNMATCHED と同じ判断)。
  [BiddingIntradayOrderStatus.UNMATCHED]: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.orderStatus.unmatched',
  },
  // 失効 (AC-BIN-019/036): GC 到達時点で JEPX の板に一度も並べなかった。
  // 終端かつ再送不能なので `negative` (SpotBidStatus.EXPIRED と同じ判断)。
  [BiddingIntradayOrderStatus.EXPIRED]: {
    status: 'negative',
    labelKey: 'page:biddingIntraday.orderStatus.expired',
  },
}
